import { PasswordInput as APasswordInput } from '@ark-ui/solid/password-input';
import { IconEyeClosed } from '@bbr/icons/eye-closed-outline';
import { IconEye } from '@bbr/icons/eye-outline';
import { children, createEffect, createSignal, Show, splitProps } from 'solid-js';
import { cn } from 'tailwind-variants';
import { Button } from './button.tsx';
import { Field } from './field.tsx';
import { disableAutocomplete } from './form.tsx';
import type { ComponentProps, JSXElement } from 'solid-js';

export function PasswordInput(props: ComponentProps<typeof APasswordInput.Root> & { name: string }) {
	return (
		<Field name={props.name}>
			<APasswordInput.Root {...props} class="flex flex-col gap-1">
				{props.children}
			</APasswordInput.Root>
		</Field>
	);
}

PasswordInput.Label = (props: ComponentProps<typeof APasswordInput.Label>) => (
	<APasswordInput.Label {...props} class={cn('text-lg font-medium', props.class)} />
);

PasswordInput.Control = (
	props: ComponentProps<typeof APasswordInput.Input> & {
		maskLabelShow?: string;
		maskLabelHide?: string;
	},
) => {
	const [, rest] = splitProps(props, ['maskLabelShow', 'maskLabelHide']);
	const [ref, setRef] = createSignal<HTMLInputElement>();

	createEffect(() => {
		const el = ref();
		if (props.autofocus && el) {
			setTimeout(() => el && el.focus());
		}
	});

	return (
		<APasswordInput.Control class="relative flex w-full items-center justify-between gap-x-1 rounded-md border border-zinc-300 bg-white ps-3 pe-0.5 text-lg transition-all outline-none group-hover/field:ring-4 group-hover/field:ring-yellow-400/30 has-focus-visible:ring-4 has-focus-visible:ring-yellow-500/50 has-focus-visible:ring-offset-1 has-focus-visible:ring-offset-zinc-50 has-disabled:bg-zinc-100 has-disabled:text-zinc-500 has-aria-invalid:border-rose-600 has-aria-readonly:cursor-not-allowed has-aria-readonly:bg-zinc-100 has-aria-readonly:text-zinc-500 data-com-onepassword-filled:bg-clip-text motion-safe:has-disabled:active:animate-shake dark:border-zinc-700 dark:bg-black dark:has-focus-visible:ring-offset-zinc-950 dark:has-disabled:bg-zinc-800">
			<APasswordInput.Input
				class="shrink grow py-2 outline-none data-com-onepassword-filled:bg-clip-text"
				tabindex={0}
				ref={setRef}
				{...rest}
				{...disableAutocomplete(props)}
			/>
			<APasswordInput.VisibilityTrigger
				asChild={(props) => (
					// @ts-expect-error too complex
					<Button {...props()} type="button" size="sm" rounding="md" appearance="subtle" />
				)}
			>
				<APasswordInput.Indicator fallback={<IconEyeClosed aria-label={props.maskLabelShow} data-icon={null} />}>
					<IconEye aria-label={props.maskLabelHide} data-icon={null} />
				</APasswordInput.Indicator>
			</APasswordInput.VisibilityTrigger>
		</APasswordInput.Control>
	);
};

export function PasswordField(
	props: ComponentProps<typeof PasswordInput.Control> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	const description = children(() => props.description);
	return (
		<PasswordInput name={rest.name}>
			<PasswordInput.Label>{other.label}</PasswordInput.Label>
			<PasswordInput.Control {...rest} />
			<Field.Errors />
			<Show when={description()}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</PasswordInput>
	);
}
